'use client'

import React, { useState, useEffect, useCallback, useRef } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { calculate, type JsonEngineConfig } from '@/core/engines/json'
import { getSudokuTranslations } from '@/lib/sudoku-translations'
import { pickRandomFlags, flagDisplayName, FLAG_SLUGS } from '@/lib/flags-data'
import { recordFlagEvents, removeFlagEvents, getFlagTotals, WINDOW_24H, WINDOW_7D, WINDOW_30D } from '@/lib/flag-stats'

type Props = {
    config: JsonEngineConfig
    interface: any
    initialValues?: any
    h1: string
    lang: string
    toolId?: string
    h1En?: string
    translations: {
        suggest: string
        getWidget: string
        downloadWidget: string
    }
    toolSlug?: string
}

type Difficulty = 'easy' | 'medium' | 'hard'
type HintTechnique = 'naked' | 'hidden' | 'direct'
type HintUnit = 'row' | 'column' | 'box'
type Hint = { row: number; col: number; digit: number; technique: HintTechnique; unit?: HintUnit; step: number }
type HistoryEntry = { row: number; col: number; prevValue: string; prevNotes: number[]; prevError: boolean; mistakeDelta: number; scoreDelta: number; streakDelta: number; flagEventId?: string }

const MAX_MISTAKES = 3

// --- Scoring system -------------------------------------------------------
// Score per correct move = (Base + Structure bonus) x Streak x Time x Difficulty
const BASE_MOVE_SCORE = 100
const STRUCTURE_BONUS = 500 // per row/column/box completed by this move (stacks)
const WRONG_PENALTY = 200
const HINT_PENALTY = 1000
const COMPLETION_BONUS = 5000
const LIFE_BONUS = 500 // per unused mistake "life" at completion
const SPEED_BONUS = 2000
const SPEED_BONUS_THRESHOLD: Record<Difficulty, number> = { easy: 300, medium: 600, hard: 900 } // seconds
const DIFFICULTY_MULTIPLIER: Record<Difficulty, number> = { easy: 1.0, medium: 1.5, hard: 2.5 }
const ALL_TIME_STORAGE_KEY = 'sudokuflags_all_time_score'
const LIKED_STORAGE_KEY = 'sudokuflags_liked'

function streakMultiplier(streak: number): number {
    if (streak >= 10) return 1.5
    if (streak >= 5) return 1.2
    return 1.0
}

function timeMultiplier(elapsedSeconds: number): number {
    const steps = Math.floor(elapsedSeconds / 30)
    return Math.max(1.0, 3.0 - steps * 0.1)
}

function parseGrid(text: string): number[][] {
    return text
        .trim()
        .split('\n')
        .map((row) => row.trim().split(/\s+/).map((c) => (c === '.' ? 0 : Number(c))))
}

function emptyNumberGrid(): number[][] {
    return Array.from({ length: 9 }, () => Array(9).fill(0))
}

function emptyStringGrid(): string[][] {
    return Array.from({ length: 9 }, () => Array(9).fill(''))
}

function emptyBoolGrid(): boolean[][] {
    return Array.from({ length: 9 }, () => Array(9).fill(false))
}

function emptyNotesGrid(): number[][][] {
    return Array.from({ length: 9 }, () => Array.from({ length: 9 }, () => [] as number[]))
}

function cloneGrid<T>(grid: T[][]): T[][] {
    return grid.map((row) => row.slice())
}

function formatTime(totalSeconds: number): string {
    const m = Math.floor(totalSeconds / 60)
    const s = totalSeconds % 60
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function boxCellsOf(row: number, col: number): Array<[number, number]> {
    const boxR = Math.floor(row / 3) * 3
    const boxC = Math.floor(col / 3) * 3
    const cells: Array<[number, number]> = []
    for (let dr = 0; dr < 3; dr++) for (let dc = 0; dc < 3; dc++) cells.push([boxR + dr, boxC + dc])
    return cells
}

// Finds a teachable next move: prefer a "naked single" (only one candidate digit
// fits the cell), then a "hidden single" (only one cell in some row/column/box
// can hold a given digit), and only fall back to a plain reveal if neither
// simple pattern applies anywhere on the board.
function findHint(known: number[][], solution: number[][]): Omit<Hint, 'step'> | null {
    const candidates: number[][][] = Array.from({ length: 9 }, () => Array.from({ length: 9 }, () => [] as number[]))
    for (let r = 0; r < 9; r++) {
        for (let c = 0; c < 9; c++) {
            if (known[r][c] !== 0) continue
            const used = new Set<number>()
            for (let i = 0; i < 9; i++) {
                if (known[r][i]) used.add(known[r][i])
                if (known[i][c]) used.add(known[i][c])
            }
            for (const [br, bc] of boxCellsOf(r, c)) {
                if (known[br][bc]) used.add(known[br][bc])
            }
            for (let n = 1; n <= 9; n++) if (!used.has(n)) candidates[r][c].push(n)
        }
    }

    const nakedCandidates: Array<[number, number, number]> = []
    for (let r = 0; r < 9; r++) {
        for (let c = 0; c < 9; c++) {
            if (known[r][c] === 0 && candidates[r][c].length === 1) nakedCandidates.push([r, c, candidates[r][c][0]])
        }
    }
    if (nakedCandidates.length > 0) {
        const [row, col, digit] = nakedCandidates[Math.floor(Math.random() * nakedCandidates.length)]
        return { row, col, digit, technique: 'naked' }
    }

    for (let r = 0; r < 9; r++) {
        for (let n = 1; n <= 9; n++) {
            const cells: number[] = []
            for (let c = 0; c < 9; c++) if (known[r][c] === 0 && candidates[r][c].includes(n)) cells.push(c)
            if (cells.length === 1) return { row: r, col: cells[0], digit: n, technique: 'hidden', unit: 'row' }
        }
    }
    for (let c = 0; c < 9; c++) {
        for (let n = 1; n <= 9; n++) {
            const cells: number[] = []
            for (let r = 0; r < 9; r++) if (known[r][c] === 0 && candidates[r][c].includes(n)) cells.push(r)
            if (cells.length === 1) return { row: cells[0], col: c, digit: n, technique: 'hidden', unit: 'column' }
        }
    }
    for (let b = 0; b < 9; b++) {
        const boxR = Math.floor(b / 3) * 3
        const boxC = (b % 3) * 3
        for (let n = 1; n <= 9; n++) {
            const cells: Array<[number, number]> = []
            for (const [r, c] of boxCellsOf(boxR, boxC)) {
                if (known[r][c] === 0 && candidates[r][c].includes(n)) cells.push([r, c])
            }
            if (cells.length === 1) return { row: cells[0][0], col: cells[0][1], digit: n, technique: 'hidden', unit: 'box' }
        }
    }

    const emptyCells: Array<[number, number]> = []
    for (let r = 0; r < 9; r++) for (let c = 0; c < 9; c++) if (known[r][c] === 0) emptyCells.push([r, c])
    if (emptyCells.length === 0) return null
    const [row, col] = emptyCells[Math.floor(Math.random() * emptyCells.length)]
    return { row, col, digit: solution[row][col], technique: 'direct' }
}

function unitCells(unit: HintUnit, row: number, col: number): Array<[number, number]> {
    if (unit === 'row') return Array.from({ length: 9 }, (_, c): [number, number] => [row, c])
    if (unit === 'column') return Array.from({ length: 9 }, (_, r): [number, number] => [r, col])
    return boxCellsOf(row, col)
}

// Visual aid for the hint carousel: lights up only the single unit (row,
// column, or box) that actually proves the hint, rather than every line
// touching every occurrence of the digit on the board - a narrower, more
// legible spotlight instead of scattering highlights across the whole grid.
function hintLitAndSourceCells(known: number[][], hint: Hint): { lit: Set<string>; sources: Set<string> } {
    const lit = new Set<string>()
    const sources = new Set<string>()

    if (hint.technique === 'hidden' && hint.unit) {
        const cells = unitCells(hint.unit, hint.row, hint.col)
        cells.forEach(([r, c]) => lit.add(`${r}-${c}`))
        for (const [r, c] of cells) {
            if (r === hint.row && c === hint.col) continue
            if (known[r][c] !== 0) continue
            for (let i = 0; i < 9; i++) {
                if (known[r][i] === hint.digit) sources.add(`${r}-${i}`)
                if (known[i][c] === hint.digit) sources.add(`${i}-${c}`)
            }
            for (const [br, bc] of boxCellsOf(r, c)) {
                if (known[br][bc] === hint.digit) sources.add(`${br}-${bc}`)
            }
        }
    } else if (hint.technique === 'naked') {
        for (let i = 0; i < 9; i++) {
            lit.add(`${hint.row}-${i}`)
            lit.add(`${i}-${hint.col}`)
            if (known[hint.row][i] !== 0) sources.add(`${hint.row}-${i}`)
            if (known[i][hint.col] !== 0) sources.add(`${i}-${hint.col}`)
        }
        for (const [r, c] of boxCellsOf(hint.row, hint.col)) {
            lit.add(`${r}-${c}`)
            if (known[r][c] !== 0) sources.add(`${r}-${c}`)
        }
    } else {
        lit.add(`${hint.row}-${hint.col}`)
    }

    sources.forEach((k) => lit.add(k))
    return { lit, sources }
}

const IconButton = ({ onClick, disabled, active, label, children }: { onClick: () => void; disabled?: boolean; active?: boolean; label: string; children: React.ReactNode }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        className={`flex items-center justify-center w-14 h-14 rounded-full bg-gray-100 transition-colors ${disabled ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-200'} ${active ? 'text-blue-600' : 'text-gray-600'}`}
    >
        {children}
    </button>
)

const NotesToggleButton = ({ onClick, disabled, active }: { onClick: () => void; disabled?: boolean; active: boolean }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label="Notes"
        className={`relative flex items-center justify-center w-14 h-14 rounded-full transition-colors ${disabled ? 'opacity-30 cursor-not-allowed' : ''} ${active ? 'bg-blue-50 ring-2 ring-blue-600 text-blue-600 hover:bg-blue-100' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
    >
        <PencilIcon />
        <span className={`absolute -top-1.5 -right-1.5 text-[8px] font-bold px-1.5 py-0.5 rounded-full leading-none ${active ? 'bg-blue-600 text-white' : 'bg-gray-300 text-white'}`}>
            {active ? 'ON' : 'OFF'}
        </span>
    </button>
)

const UndoIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 10h10a5 5 0 0 1 0 10h-2" />
        <path d="M7 6 3 10l4 4" />
    </svg>
)
const EraserIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 13 9 22H4a2 2 0 0 1-1.4-3.4l11-11a2 2 0 0 1 2.8 0l3.6 3.6a2 2 0 0 1 0 2.8L14 20" />
    </svg>
)
const PencilIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 20.4 4.9 16 15.7 5.2a2.1 2.1 0 0 1 3 0l.1.1a2.1 2.1 0 0 1 0 3L8 19.1l-4 1.3Z" />
        <path d="m13.6 6.7 3.7 3.7" />
        <path d="M4 20.4 6.9 19.5" />
    </svg>
)
const LightbulbIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 18h6" />
        <path d="M10 22h4" />
        <path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2Z" />
    </svg>
)
const PlayIcon = () => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5v14l11-7Z" />
    </svg>
)
const PauseIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <rect x="6" y="4" width="4" height="16" rx="1" />
        <rect x="14" y="4" width="4" height="16" rx="1" />
    </svg>
)
const TrophyIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 21h8" />
        <path d="M12 17v4" />
        <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
        <path d="M17 5h3a2 2 0 0 1-2 5.5" />
        <path d="M7 5H4a2 2 0 0 0 2 5.5" />
    </svg>
)
const SwapIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m17 2 4 4-4 4" />
        <path d="M3 11V9a4 4 0 0 1 4-4h14" />
        <path d="m7 22-4-4 4-4" />
        <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
)
const SuggestIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 18h6" />
        <path d="M10 22h4" />
        <path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2Z" />
    </svg>
)
const ShareIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="18" cy="5" r="3" />
        <circle cx="6" cy="12" r="3" />
        <circle cx="18" cy="19" r="3" />
        <path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" />
    </svg>
)
const SaveAppIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3v12" />
        <path d="m7 10 5 5 5-5" />
        <path d="M5 21h14" />
    </svg>
)
const HeartIcon = ({ filled }: { filled: boolean }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.8 1-1a5.5 5.5 0 0 0 0-7.8Z" />
    </svg>
)

// One board digit/number-pad button, rendered as a coin-flip tile: the flag
// is the "front" face, the plain digit is the "back" face - toggling
// viewMode rotates the whole tile 180deg so the correct face shows.
const FlipTile = ({ digit, flagSlug, showFlag, textClassName }: { digit: number; flagSlug: string; showFlag: boolean; textClassName?: string }) => (
    <span className="relative inline-block w-full h-full [perspective:600px]">
        <span
            className="absolute inset-0 transition-transform duration-500 [transform-style:preserve-3d]"
            style={{ transform: showFlag ? 'rotateY(0deg)' : 'rotateY(180deg)' }}
        >
            <span className="absolute inset-0 flex items-center justify-center p-[8%] [backface-visibility:hidden]">
                <img src={`/flags/${flagSlug}.svg`} alt={flagDisplayName(flagSlug)} className="w-full h-full object-contain rounded-sm" draggable={false} />
            </span>
            <span className={`absolute inset-0 flex items-center justify-center [backface-visibility:hidden] [transform:rotateY(180deg)] ${textClassName || ''}`}>
                {digit}
            </span>
        </span>
    </span>
)

export default function SudokuFlagsWidget({ config, h1, lang, translations, toolSlug }: Props) {
    const t = getSudokuTranslations(lang)
    const [difficulty, setDifficulty] = useState<Difficulty>('easy')
    const [givenGrid, setGivenGrid] = useState<number[][]>(emptyNumberGrid())
    const [solutionGrid, setSolutionGrid] = useState<number[][]>(emptyNumberGrid())
    const [values, setValues] = useState<string[][]>(emptyStringGrid())
    const [notesGrid, setNotesGrid] = useState<number[][][]>(emptyNotesGrid())
    const [errorGrid, setErrorGrid] = useState<boolean[][]>(emptyBoolGrid())
    const [history, setHistory] = useState<HistoryEntry[]>([])
    const [mistakes, setMistakes] = useState(0)
    const [isGameOver, setIsGameOver] = useState(false)
    const [solved, setSolved] = useState(false)
    const [selected, setSelected] = useState<[number, number] | null>(null)
    const [notesMode, setNotesMode] = useState(false)
    const [hint, setHint] = useState<Hint | null>(null)
    const [elapsedSeconds, setElapsedSeconds] = useState(0)
    const [isPaused, setIsPaused] = useState(false)
    const [shimmerCells, setShimmerCells] = useState<Set<string>>(new Set())

    // Deterministic placeholder for the very first (server-rendered) paint -
    // actual randomization only happens client-side in generate(), called
    // from the mount effect below, exactly like givenGrid/solutionGrid avoid
    // an SSR/hydration mismatch by starting empty and filling in on mount.
    const [boardFlags, setBoardFlags] = useState<string[]>(() => FLAG_SLUGS.slice(0, 9))
    const [viewMode, setViewMode] = useState<'flags' | 'numbers'>('flags')
    const [showStats, setShowStats] = useState(false)
    const [statsWindow, setStatsWindow] = useState<'24h' | '7d' | '30d' | 'all'>('all')
    const [sessionScore, setSessionScore] = useState(0)
    const [currentStreak, setCurrentStreak] = useState(0)
    const [allTimeBase, setAllTimeBase] = useState(0)
    // Guards the very first generate() call (on mount) from folding a
    // not-yet-loaded allTimeBase (still 0 in that render's closure) back into
    // localStorage and stomping the real persisted value before it's read.
    const hasStartedRef = useRef(false)

    // Action row: like (local-only, per-device), share, and "save as app".
    const [liked, setLiked] = useState(false)
    const [toastMessage, setToastMessage] = useState<string | null>(null)
    // Captured `beforeinstallprompt` event (Chrome/Android only - iOS Safari
    // never fires it, so "Save as App" there just shows manual instructions).
    const deferredInstallPromptRef = useRef<any>(null)

    useEffect(() => {
        try {
            setLiked(window.localStorage.getItem(LIKED_STORAGE_KEY) === '1')
        } catch {
            // ignore
        }
    }, [])

    useEffect(() => {
        const onBeforeInstallPrompt = (event: Event) => {
            event.preventDefault()
            deferredInstallPromptRef.current = event
        }
        window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
        return () => window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    }, [])

    useEffect(() => {
        if (!toastMessage) return
        const id = setTimeout(() => setToastMessage(null), 2500)
        return () => clearTimeout(id)
    }, [toastMessage])

    const handleShare = useCallback(async () => {
        const shareUrl = window.location.href
        try {
            if (navigator.share) {
                await navigator.share({ title: h1, url: shareUrl })
                return
            }
        } catch {
            // user cancelled the native share sheet - fall through to clipboard
        }
        try {
            await navigator.clipboard.writeText(shareUrl)
            setToastMessage(t.linkCopied)
        } catch {
            // clipboard unavailable - nothing else we can do
        }
    }, [h1, t.linkCopied])

    const handleSaveAsApp = useCallback(async () => {
        const promptEvent = deferredInstallPromptRef.current
        if (promptEvent) {
            deferredInstallPromptRef.current = null
            promptEvent.prompt()
            return
        }
        setToastMessage(t.installInstructions)
    }, [t.installInstructions])

    const handleLike = useCallback(() => {
        setLiked((prev) => {
            const next = !prev
            try {
                window.localStorage.setItem(LIKED_STORAGE_KEY, next ? '1' : '0')
            } catch {
                // ignore
            }
            return next
        })
    }, [])

    const persistAllTime = useCallback((total: number) => {
        setAllTimeBase(total)
        try {
            window.localStorage.setItem(ALL_TIME_STORAGE_KEY, String(Math.max(0, Math.round(total))))
        } catch {
            // ignore
        }
    }, [])

    const generate = useCallback((diff: Difficulty) => {
        // Fold whatever the current session earned into the persisted total
        // before starting a fresh board (points from an abandoned game stay
        // earned; only the completion bonuses require actually finishing) -
        // skipped on the very first (mount-time) call, see hasStartedRef.
        if (hasStartedRef.current) {
            persistAllTime(allTimeBase + sessionScore)
        } else {
            hasStartedRef.current = true
            try {
                const stored = Number(window.localStorage.getItem(ALL_TIME_STORAGE_KEY))
                if (!Number.isNaN(stored) && stored > 0) setAllTimeBase(stored)
            } catch {
                // localStorage unavailable (private mode, etc.) - all-time just won't persist
            }
        }

        const result = calculate(config, { difficulty: diff }) as any
        const puzzle = parseGrid(String(result.puzzle || ''))
        const solution = parseGrid(String(result.solution || ''))
        setGivenGrid(puzzle)
        setSolutionGrid(solution)
        setValues(puzzle.map((row) => row.map((v) => (v === 0 ? '' : String(v)))))
        setNotesGrid(emptyNotesGrid())
        setErrorGrid(emptyBoolGrid())
        setHistory([])
        setMistakes(0)
        setIsGameOver(false)
        setSolved(false)
        setSelected(null)
        setNotesMode(false)
        setHint(null)
        setElapsedSeconds(0)
        setIsPaused(false)
        setShimmerCells(new Set())
        setBoardFlags(pickRandomFlags())
        setSessionScore(0)
        setCurrentStreak(0)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config, allTimeBase, sessionScore, persistAllTime])

    useEffect(() => {
        generate(difficulty)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (isPaused || isGameOver || solved) return
        const id = setInterval(() => setElapsedSeconds((s) => s + 1), 1000)
        return () => clearInterval(id)
    }, [isPaused, isGameOver, solved])

    const handleDifficultyChange = (diff: Difficulty) => {
        setDifficulty(diff)
        generate(diff)
    }

    const computeKnownGrid = useCallback((): number[][] => {
        const known = emptyNumberGrid()
        for (let r = 0; r < 9; r++) {
            for (let c = 0; c < 9; c++) {
                if (givenGrid[r][c] !== 0) known[r][c] = givenGrid[r][c]
                else if (values[r][c] !== '' && !errorGrid[r][c]) known[r][c] = Number(values[r][c])
            }
        }
        return known
    }, [givenGrid, values, errorGrid])

    const isUnitComplete = (vals: string[][], cells: Array<[number, number]>): boolean =>
        cells.every(([r, c]) => {
            const v = givenGrid[r][c] !== 0 ? givenGrid[r][c] : Number(vals[r][c] || 0)
            return v === solutionGrid[r][c]
        })

    // Counts how many of the row/column/box touching this cell were just
    // completed by this move - feeds both the shimmer animation and the
    // structure-completion score bonus (stacks when a move closes more than one).
    const countCompletedUnits = (row: number, col: number, vals: string[][]): number => {
        const rowCells = Array.from({ length: 9 }, (_, c): [number, number] => [row, c])
        const colCells = Array.from({ length: 9 }, (_, r): [number, number] => [r, col])
        const boxCells = boxCellsOf(row, col)
        let count = 0
        if (isUnitComplete(vals, rowCells)) count++
        if (isUnitComplete(vals, colCells)) count++
        if (isUnitComplete(vals, boxCells)) count++
        return count
    }

    const triggerCompletionAnimations = (row: number, col: number, vals: string[][]) => {
        const rowCells = Array.from({ length: 9 }, (_, c): [number, number] => [row, c])
        const colCells = Array.from({ length: 9 }, (_, r): [number, number] => [r, col])
        const boxCells = boxCellsOf(row, col)

        const cells = new Set<string>()
        if (isUnitComplete(vals, rowCells)) rowCells.forEach(([r, c]) => cells.add(`${r}-${c}`))
        if (isUnitComplete(vals, colCells)) colCells.forEach(([r, c]) => cells.add(`${r}-${c}`))
        if (isUnitComplete(vals, boxCells)) boxCells.forEach(([r, c]) => cells.add(`${r}-${c}`))

        if (cells.size > 0) {
            setShimmerCells(cells)
            setTimeout(() => setShimmerCells(new Set()), 2000)
        }
    }

    const checkWin = (vals: string[][]) => {
        for (let r = 0; r < 9; r++) {
            for (let c = 0; c < 9; c++) {
                const v = givenGrid[r][c] !== 0 ? givenGrid[r][c] : Number(vals[r][c] || 0)
                if (v !== solutionGrid[r][c]) return
            }
        }
        setSolved(true)
        const livesBonus = (MAX_MISTAKES - mistakes) * LIFE_BONUS
        const speedBonus = elapsedSeconds < SPEED_BONUS_THRESHOLD[difficulty] ? SPEED_BONUS : 0
        const totalBonus = COMPLETION_BONUS + livesBonus + speedBonus
        setSessionScore((s) => s + totalBonus)

        // Completion bonus is split evenly across all 9 flags on the board
        // (docs/SUDOKU_FLAGS_LEADERBOARD.md) - not undo-reversible, same
        // known simplification as sessionScore's own completion bonus.
        const perFlag = Math.round(totalBonus / 9)
        recordFlagEvents(boardFlags.map((flagSlug) => ({ flagSlug, points: perFlag })))
    }

    // Applies a value/notes/error change to one cell, records it on the undo
    // stack (including its score/streak deltas), and updates the mistake
    // count - returns the resulting grid so callers can immediately run
    // completion/win checks against fresh data instead of racing React's
    // async state updates.
    const applyAndCommit = (row: number, col: number, newValue: string, newNotes: number[], newError: boolean, mistakeDelta: number, scoreDelta: number, streakDelta: number, flagEvent?: { flagSlug: string; points: number }): string[][] => {
        const prevValue = values[row][col]
        const prevNotes = notesGrid[row][col]
        const prevError = errorGrid[row][col]

        const nextValues = cloneGrid(values)
        nextValues[row][col] = newValue
        setValues(nextValues)

        const nextNotes = notesGrid.map((r) => r.map((cell) => cell.slice()))
        nextNotes[row][col] = newNotes
        setNotesGrid(nextNotes)

        const nextError = cloneGrid(errorGrid)
        nextError[row][col] = newError
        setErrorGrid(nextError)

        // Phase 1 per-flag stats (docs/SUDOKU_FLAGS_LEADERBOARD.md): correct/
        // wrong moves attribute their points to the flag placed; hints and
        // notes/eraser don't touch any flag's tally (flagEvent left undefined).
        const [createdEvent] = flagEvent ? recordFlagEvents([flagEvent]) : []

        setHistory((h) => [...h, { row, col, prevValue, prevNotes, prevError, mistakeDelta, scoreDelta, streakDelta, flagEventId: createdEvent?.id }])

        if (mistakeDelta > 0) {
            setMistakes((m) => {
                const next = m + mistakeDelta
                if (next >= MAX_MISTAKES) setIsGameOver(true)
                return next
            })
        }
        if (scoreDelta !== 0) setSessionScore((s) => s + scoreDelta)
        if (streakDelta !== 0) setCurrentStreak((s) => Math.max(0, s + streakDelta))

        return nextValues
    }

    const handleCellClick = (row: number, col: number) => {
        if (isPaused || hint) return
        setSelected([row, col])
    }

    const handleNumberPadClick = (n: number) => {
        if (!selected || isPaused || isGameOver || solved || hint) return
        const [row, col] = selected
        if (givenGrid[row][col] !== 0) return

        if (notesMode) {
            if (values[row][col] !== '') return
            const prevNotes = notesGrid[row][col]
            const newNotes = prevNotes.includes(n) ? prevNotes.filter((x) => x !== n) : [...prevNotes, n].sort((a, b) => a - b)
            applyAndCommit(row, col, '', newNotes, false, 0, 0, 0)
            return
        }

        const isCorrect = n === solutionGrid[row][col]
        if (isCorrect) {
            const nextValuesPreview = cloneGrid(values)
            nextValuesPreview[row][col] = String(n)
            const completedUnits = countCompletedUnits(row, col, nextValuesPreview)
            const structureBonus = completedUnits * STRUCTURE_BONUS
            const newStreak = currentStreak + 1
            const moveScore = Math.round((BASE_MOVE_SCORE + structureBonus) * streakMultiplier(newStreak) * timeMultiplier(elapsedSeconds) * DIFFICULTY_MULTIPLIER[difficulty])
            const nextValues = applyAndCommit(row, col, String(n), [], false, 0, moveScore, 1, { flagSlug: boardFlags[n - 1], points: moveScore })
            triggerCompletionAnimations(row, col, nextValues)
            checkWin(nextValues)
        } else {
            applyAndCommit(row, col, String(n), [], true, 1, -WRONG_PENALTY, -currentStreak, { flagSlug: boardFlags[n - 1], points: -WRONG_PENALTY })
        }
    }

    const handleEraser = () => {
        if (!selected || isPaused || isGameOver || hint) return
        const [row, col] = selected
        if (givenGrid[row][col] !== 0) return
        if (values[row][col] === '' && notesGrid[row][col].length === 0) return
        applyAndCommit(row, col, '', [], false, 0, 0, 0)
    }

    const handleUndo = () => {
        if (history.length === 0 || isPaused || hint) return
        const last = history[history.length - 1]
        setHistory((h) => h.slice(0, -1))

        const nextValues = cloneGrid(values)
        nextValues[last.row][last.col] = last.prevValue
        setValues(nextValues)

        const nextNotes = notesGrid.map((r) => r.map((cell) => cell.slice()))
        nextNotes[last.row][last.col] = last.prevNotes
        setNotesGrid(nextNotes)

        const nextError = cloneGrid(errorGrid)
        nextError[last.row][last.col] = last.prevError
        setErrorGrid(nextError)

        if (last.mistakeDelta > 0) {
            setMistakes((m) => Math.max(0, m - last.mistakeDelta))
            setIsGameOver(false)
        }
        if (last.scoreDelta !== 0) setSessionScore((s) => s - last.scoreDelta)
        if (last.streakDelta !== 0) setCurrentStreak((s) => Math.max(0, s - last.streakDelta))
        if (last.flagEventId) removeFlagEvents([last.flagEventId])
        setSolved(false)
    }

    const handleHintClick = () => {
        if (isPaused || isGameOver || solved || hint) return
        const known = computeKnownGrid()
        const found = findHint(known, solutionGrid)
        if (!found) return
        setSelected([found.row, found.col])
        setHint({ ...found, step: 0 })
    }
    const handleHintNext = () => setHint((h) => (h ? { ...h, step: Math.min(h.step + 1, 2) } : h))
    const handleHintPrev = () => setHint((h) => (h ? { ...h, step: Math.max(h.step - 1, 0) } : h))
    const handleHintConfirm = () => {
        if (!hint) return
        const nextValues = applyAndCommit(hint.row, hint.col, String(hint.digit), [], false, 0, -HINT_PENALTY, 0)
        setHint(null)
        triggerCompletionAnimations(hint.row, hint.col, nextValues)
        checkWin(nextValues)
    }
    const handleHintClose = () => setHint(null)

    const hintTitle = (h: Hint): string => (h.technique === 'direct' ? t.hintTitleDirect : t.hintTitleOnlyCandidate)
    const hintStepText = (h: Hint): string => {
        if (h.step === 0) return t.hintStep1
        const unitWord = h.unit === 'row' ? t.unitRow : h.unit === 'column' ? t.unitColumn : t.unitBlock
        if (h.technique === 'hidden') {
            const template = h.step === 1 ? t.hintStep2LastRemaining : t.hintStep3LastRemaining
            return template.replace('{unit}', unitWord).replace('{n}', String(h.digit))
        }
        if (h.technique === 'naked') {
            const template = h.step === 1 ? t.hintStep2OnlyCandidate : t.hintStep3OnlyCandidate
            return template.replace(/\{n\}/g, String(h.digit))
        }
        const template = h.step === 1 ? t.hintStep2Direct : t.hintStep3Direct
        return template.replace('{n}', String(h.digit))
    }

    const selectedValue = selected ? (givenGrid[selected[0]][selected[1]] !== 0 ? givenGrid[selected[0]][selected[1]] : Number(values[selected[0]][selected[1]] || 0)) : 0
    const hintVisual = hint ? hintLitAndSourceCells(computeKnownGrid(), hint) : null

    const renderCellContent = (row: number, col: number) => {
        if (isPaused) return null
        if (hint && hint.step === 2 && hint.row === row && hint.col === col) {
            return <FlipTile digit={hint.digit} flagSlug={boardFlags[hint.digit - 1]} showFlag={viewMode === 'flags'} textClassName="text-blue-600 font-bold" />
        }
        const given = givenGrid[row][col]
        if (given !== 0) return <FlipTile digit={given} flagSlug={boardFlags[given - 1]} showFlag={viewMode === 'flags'} textClassName="text-gray-900 font-bold" />
        if (errorGrid[row][col] && values[row][col] !== '') {
            return <span className="text-red-600 font-bold">{values[row][col]}</span>
        }
        if (values[row][col] !== '') {
            const n = Number(values[row][col])
            return <FlipTile digit={n} flagSlug={boardFlags[n - 1]} showFlag={viewMode === 'flags'} textClassName="text-blue-600 font-semibold" />
        }
        const notes = notesGrid[row][col]
        if (notes.length === 0) return null
        return (
            <div className="grid grid-cols-3 grid-rows-3 w-full h-full text-[10px] lg:text-xs leading-none text-gray-400">
                {Array.from({ length: 9 }, (_, i) => i + 1).map((n) => (
                    <div key={n} className="flex items-center justify-center">{notes.includes(n) ? n : ''}</div>
                ))}
            </div>
        )
    }

    // Border classes for one cell, normally just the standard 3x3-box grid
    // lines - but while a hint is on step 1+, the box containing the target
    // cell instead gets a bold blue outline on whichever edges face outward.
    const cellBorders = (row: number, col: number): string => {
        let boxEdges: { top: boolean; bottom: boolean; left: boolean; right: boolean } | null = null
        if (hint && hint.step >= 1) {
            const boxR = Math.floor(hint.row / 3) * 3
            const boxC = Math.floor(hint.col / 3) * 3
            if (row >= boxR && row < boxR + 3 && col >= boxC && col < boxC + 3) {
                boxEdges = { top: row === boxR, bottom: row === boxR + 2, left: col === boxC, right: col === boxC + 2 }
            }
        }
        const top = boxEdges?.top ? 'border-t-4 border-t-blue-700' : row % 3 === 0 ? 'border-t-2 border-t-gray-800' : 'border-t border-t-gray-300'
        const left = boxEdges?.left ? 'border-l-4 border-l-blue-700' : col % 3 === 0 ? 'border-l-2 border-l-gray-800' : 'border-l border-l-gray-300'
        const right = boxEdges?.right ? 'border-r-4 border-r-blue-700' : col === 8 ? 'border-r-2 border-r-gray-800' : ''
        const bottom = boxEdges?.bottom ? 'border-b-4 border-b-blue-700' : row === 8 ? 'border-b-2 border-b-gray-800' : ''
        return `${top} ${left} ${right} ${bottom}`
    }

    const cellClasses = (row: number, col: number): string => {
        const key = `${row}-${col}`
        const isGiven = givenGrid[row][col] !== 0
        const isError = errorGrid[row][col]
        const isSelected = !!selected && selected[0] === row && selected[1] === col
        const isPeer = !!selected && (selected[0] === row || selected[1] === col || (Math.floor(selected[0] / 3) === Math.floor(row / 3) && Math.floor(selected[1] / 3) === Math.floor(col / 3)))
        const displayValue = isGiven ? givenGrid[row][col] : Number(values[row][col] || 0)
        const isSameValue = !isSelected && selectedValue !== 0 && displayValue === selectedValue

        const borders = cellBorders(row, col)

        if (shimmerCells.has(key)) return `sudoku-shimmer ${borders}`

        if (hint && hintVisual) {
            const isSource = hintVisual.sources.has(key)
            const isLit = hintVisual.lit.has(key)
            const isTarget = hint.row === row && hint.col === col
            if (!isLit) return `bg-gray-800 ${borders}`
            if (isSource) return `bg-green-200 ${borders}`
            if (isTarget && hint.step >= 1) return `bg-white ${borders}`
            return `bg-blue-100 ${borders}`
        }

        if (isError) return `bg-white ${borders}`
        const bg = isSelected ? 'bg-blue-200' : isSameValue ? 'bg-blue-100' : isPeer ? 'bg-blue-50' : 'bg-white'
        return `${bg} ${borders}`
    }

    const canPlay = !isPaused && !isGameOver && !solved && !hint
    const allTimeDisplay = Math.max(0, Math.round(allTimeBase + sessionScore))

    return (
        <div className="w-full lg:w-[1040px] mb-5 relative mx-auto" style={{ maxWidth: '1040px' }}>
            <div className="bg-blue-600 text-white px-5 py-3 rounded-t-lg mx-auto" style={{ width: '100%', maxWidth: '1040px' }}>
                <h2 className="hidden lg:block text-lg font-bold">{h1}</h2>

                {/* Mobile: the stats row (difficulty/all-time/mistakes/time)
                    lives in the header instead of the title, freeing up
                    vertical space in the content box for taller number-pad
                    buttons below. */}
                <div className="lg:hidden flex items-start justify-between gap-2">
                    <div>
                        <div className="text-xs text-blue-100">{t.difficultyLabel}</div>
                        <select
                            className="text-sm font-semibold outline-none bg-transparent -ml-1 p-1 rounded text-white hover:bg-white/10"
                            aria-label={t.difficultyLabel}
                            value={difficulty}
                            onChange={(e) => handleDifficultyChange(e.target.value as Difficulty)}
                        >
                            <option value="easy" className="text-gray-900">{t.easy}</option>
                            <option value="medium" className="text-gray-900">{t.medium}</option>
                            <option value="hard" className="text-gray-900">{t.hard}</option>
                        </select>
                    </div>
                    <button type="button" onClick={() => setShowStats(true)} className="text-center hover:opacity-70 transition-opacity">
                        <div className="text-xs text-blue-100 flex items-center justify-center gap-1">
                            <TrophyIcon /> {t.allTimeLabel}
                        </div>
                        <div className="text-sm font-semibold text-white">{allTimeDisplay.toLocaleString()}</div>
                    </button>
                    <div className="text-center">
                        <div className="text-xs text-blue-100">{t.mistakesLabel}</div>
                        <div className={`text-sm font-semibold ${mistakes > 0 ? 'text-red-200' : 'text-white'}`}>{mistakes}/{MAX_MISTAKES}</div>
                    </div>
                    <div className="flex items-start gap-2">
                        <div className="text-right">
                            <div className="text-xs text-blue-100">{t.timeLabel}</div>
                            <div className="text-sm font-semibold text-white">{formatTime(elapsedSeconds)}</div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setIsPaused((p) => !p)}
                            disabled={isGameOver || solved}
                            aria-label={t.timeLabel}
                            className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center disabled:opacity-40 text-white"
                        >
                            <PauseIcon />
                        </button>
                    </div>
                </div>
            </div>

            <div className="bg-white border border-gray-200 border-t-0 p-3 lg:p-5 mx-auto relative" style={{ width: '100%', maxWidth: '1040px' }}>
                <div className="lg:flex lg:items-stretch lg:gap-6">
                    {/* Board column */}
                    <div className="lg:shrink-0">
                        <div className="flex justify-center mb-4 lg:mb-0 relative mx-[-9px] lg:mx-0">
                            <div className="grid grid-cols-9 grid-rows-[repeat(9,minmax(0,1fr))] aspect-square w-full lg:aspect-auto lg:w-fit" data-testid="sudoku-grid">
                                {givenGrid.map((row, r) =>
                                    row.map((_, c) => (
                                        <button
                                            key={`${r}-${c}`}
                                            type="button"
                                            onClick={() => handleCellClick(r, c)}
                                            className={`w-full h-full lg:w-14 lg:h-14 flex items-center justify-center text-lg lg:text-2xl relative ${cellClasses(r, c)}`}
                                        >
                                            {renderCellContent(r, c)}
                                        </button>
                                    ))
                                )}
                            </div>

                            {isPaused && (
                                <div className="absolute inset-0 flex items-center justify-center bg-white/90">
                                    <button
                                        type="button"
                                        onClick={() => setIsPaused(false)}
                                        aria-label="Resume"
                                        className="w-16 h-16 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center shadow-lg"
                                    >
                                        <PlayIcon />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Controls column - lg:flex-col + lg:h-full so its bottom
                        edge (the New Game button) lines up with the board's
                        bottom edge, matching the stretched row height above. */}
                    <div className="lg:flex-1 lg:min-w-0 lg:flex lg:flex-col lg:h-full">
                        <div className="hidden lg:flex items-start justify-between mb-4 gap-2">
                            <div>
                                <div className="text-xs text-gray-500">{t.difficultyLabel}</div>
                                <select
                                    className="text-sm font-semibold outline-none bg-transparent -ml-1 p-1 rounded hover:bg-gray-100"
                                    aria-label={t.difficultyLabel}
                                    value={difficulty}
                                    onChange={(e) => handleDifficultyChange(e.target.value as Difficulty)}
                                >
                                    <option value="easy">{t.easy}</option>
                                    <option value="medium">{t.medium}</option>
                                    <option value="hard">{t.hard}</option>
                                </select>
                            </div>
                            <button type="button" onClick={() => setShowStats(true)} className="text-center hover:opacity-70 transition-opacity">
                                <div className="text-xs text-gray-500 flex items-center justify-center gap-1">
                                    <TrophyIcon /> {t.allTimeLabel}
                                </div>
                                <div className="text-sm font-semibold text-gray-900">{allTimeDisplay.toLocaleString()}</div>
                            </button>
                            <div className="text-center">
                                <div className="text-xs text-gray-500">{t.mistakesLabel}</div>
                                <div className={`text-sm font-semibold ${mistakes > 0 ? 'text-red-600' : 'text-gray-900'}`}>{mistakes}/{MAX_MISTAKES}</div>
                            </div>
                            <div className="flex items-start gap-2">
                                <div className="text-right">
                                    <div className="text-xs text-gray-500">{t.timeLabel}</div>
                                    <div className="text-sm font-semibold text-gray-900">{formatTime(elapsedSeconds)}</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setIsPaused((p) => !p)}
                                    disabled={isGameOver || solved}
                                    aria-label={t.timeLabel}
                                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-40"
                                >
                                    <PauseIcon />
                                </button>
                            </div>
                        </div>

                        {solved && <div className="text-center text-green-700 font-bold mb-3">{t.solved}</div>}
                        {isGameOver && !solved && <div className="text-center text-red-600 font-bold mb-3">{t.gameOver}</div>}

                        {hint ? (
                            <div className="mb-4 border border-gray-200 rounded-lg p-3 lg:flex-1 lg:flex lg:flex-col lg:justify-center">
                                <div className="text-center font-bold text-gray-900 mb-1">{hintTitle(hint)}</div>
                                <div className="text-center text-sm text-gray-600 mb-3 min-h-[40px]">{hintStepText(hint)}</div>
                                <div className="flex items-center justify-center gap-4">
                                    <button
                                        type="button"
                                        onClick={handleHintPrev}
                                        disabled={hint.step === 0}
                                        aria-label="Previous"
                                        className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-30 text-gray-700"
                                    >
                                        ←
                                    </button>
                                    <div className="flex gap-1.5">
                                        {[0, 1, 2].map((i) => (
                                            <span key={i} className={`w-2 h-2 rounded-full ${i === hint.step ? 'bg-blue-600' : 'bg-gray-300'}`} />
                                        ))}
                                    </div>
                                    {hint.step < 2 ? (
                                        <button
                                            type="button"
                                            onClick={handleHintNext}
                                            aria-label="Next"
                                            className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700"
                                        >
                                            →
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleHintConfirm}
                                            aria-label="Confirm"
                                            className="w-9 h-9 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white"
                                        >
                                            ✓
                                        </button>
                                    )}
                                </div>
                                <button type="button" onClick={handleHintClose} className="block mx-auto mt-2 text-xs text-gray-400 hover:text-gray-600">
                                    ✕
                                </button>
                            </div>
                        ) : (
                            <div className="lg:flex-1 lg:flex lg:flex-col lg:min-h-0">
                                <div className="flex justify-center lg:justify-start gap-1 mb-3">
                                    <IconButton onClick={handleUndo} disabled={history.length === 0 || isPaused} label="Undo">
                                        <UndoIcon />
                                    </IconButton>
                                    <IconButton onClick={handleEraser} disabled={!selected || isPaused || isGameOver} label="Erase">
                                        <EraserIcon />
                                    </IconButton>
                                    <NotesToggleButton onClick={() => setNotesMode((m) => !m)} disabled={isPaused || isGameOver} active={notesMode} />
                                    <IconButton onClick={handleHintClick} disabled={!canPlay} label="Hint">
                                        <LightbulbIcon />
                                    </IconButton>
                                </div>

                                {/* Mobile: 3x3 grid, flag centered on top (2-4px from the
                                    button's top edge), localized country name centered
                                    below spanning the button's full width. */}
                                <div className="lg:hidden grid grid-cols-3 gap-2 mb-4 mx-[-9px] lg:mx-0" data-testid="sudoku-numberpad">
                                    {Array.from({ length: 9 }, (_, i) => i + 1).map((n) => (
                                        <button
                                            key={n}
                                            type="button"
                                            onClick={() => handleNumberPadClick(n)}
                                            disabled={!canPlay}
                                            aria-label={viewMode === 'flags' ? flagDisplayName(boardFlags[n - 1]) : String(n)}
                                            className={`flex flex-col items-center gap-1 min-w-0 pt-1 pb-1.5 px-1 rounded-xl transition-colors disabled:opacity-30 ${selectedValue !== 0 && selectedValue === n ? 'bg-blue-50 ring-2 ring-blue-600' : 'bg-gray-100 hover:bg-gray-200'}`}
                                        >
                                            <span className="w-12 h-12 shrink-0 relative"><FlipTile digit={n} flagSlug={boardFlags[n - 1]} showFlag={viewMode === 'flags'} /></span>
                                            <span className="w-full text-[12px] font-semibold text-gray-800 leading-tight line-clamp-2 text-center break-words">{flagDisplayName(boardFlags[n - 1])}</span>
                                        </button>
                                    ))}
                                </div>

                                {/* Desktop: 5 rows x 2 columns - slot 1 is the view toggle,
                                    slots 2-10 are the 9 flags, each with its localized
                                    country name (left-aligned, vertically centered, always
                                    truncated so long names never overflow the button). */}
                                <div className="hidden lg:grid lg:grid-cols-2 lg:grid-rows-5 lg:gap-2 lg:mb-4 lg:flex-1 lg:min-h-0" data-testid="sudoku-numberpad-desktop">
                                    <button
                                        type="button"
                                        onClick={() => setViewMode((m) => (m === 'flags' ? 'numbers' : 'flags'))}
                                        className="min-w-0 min-h-0 flex items-center gap-2 px-3 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors text-left text-gray-700"
                                    >
                                        <span className="shrink-0"><SwapIcon /></span>
                                        <span className="text-sm font-semibold truncate">{viewMode === 'flags' ? t.classicView : t.flagView}</span>
                                    </button>
                                    {Array.from({ length: 9 }, (_, i) => i + 1).map((n) => (
                                        <button
                                            key={n}
                                            type="button"
                                            onClick={() => handleNumberPadClick(n)}
                                            disabled={!canPlay}
                                            aria-label={viewMode === 'flags' ? flagDisplayName(boardFlags[n - 1]) : String(n)}
                                            className={`min-w-0 min-h-0 flex items-center gap-2 px-3 rounded-xl transition-colors text-left relative disabled:opacity-30 ${selectedValue !== 0 && selectedValue === n ? 'bg-blue-50 ring-2 ring-blue-600' : 'bg-gray-100 hover:bg-gray-200'}`}
                                        >
                                            <span className="w-14 h-14 shrink-0 relative"><FlipTile digit={n} flagSlug={boardFlags[n - 1]} showFlag={viewMode === 'flags'} /></span>
                                            <span className="text-sm font-semibold text-gray-800 line-clamp-3 min-w-0">{flagDisplayName(boardFlags[n - 1])}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="lg:hidden flex gap-2 mb-2 mx-[-9px] lg:mx-0">
                            <button
                                onClick={() => setViewMode((m) => (m === 'flags' ? 'numbers' : 'flags'))}
                                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-2.5 px-3 rounded-md transition-colors text-sm"
                            >
                                {viewMode === 'flags' ? t.classicView : t.flagView}
                            </button>
                            <button
                                onClick={() => generate(difficulty)}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-3 rounded-md transition-colors text-sm"
                            >
                                {t.newGame}
                            </button>
                        </div>
                        <div className="hidden lg:block mb-2">
                            <button
                                onClick={() => generate(difficulty)}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-3 rounded-md transition-colors text-sm"
                            >
                                {t.newGame}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="mt-5">
                    <div className="flex items-center justify-center gap-6">
                        <Link
                            href={`/${lang}/contact`}
                            aria-label={translations.suggest}
                            title={translations.suggest}
                            className="text-gray-500 hover:text-blue-600 transition-colors"
                        >
                            <SuggestIcon />
                        </Link>
                        <button
                            type="button"
                            onClick={handleShare}
                            aria-label={t.shareLabel}
                            title={t.shareLabel}
                            className="text-gray-500 hover:text-blue-600 transition-colors"
                        >
                            <ShareIcon />
                        </button>
                        <button
                            type="button"
                            onClick={handleSaveAsApp}
                            aria-label={t.saveAsAppLabel}
                            title={t.saveAsAppLabel}
                            className="text-gray-500 hover:text-blue-600 transition-colors"
                        >
                            <SaveAppIcon />
                        </button>
                        <button
                            type="button"
                            onClick={handleLike}
                            aria-label={t.likeLabel}
                            title={t.likeLabel}
                            className={liked ? 'text-red-500 transition-colors' : 'text-gray-500 hover:text-red-500 transition-colors'}
                        >
                            <HeartIcon filled={liked} />
                        </button>
                    </div>
                    {toastMessage && (
                        <div className="mt-2 text-center text-xs text-gray-600" role="status">
                            {toastMessage}
                        </div>
                    )}
                    {toolSlug && (
                        <Link
                            href={`/${lang}/widget/${toolSlug}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline text-sm block text-center mt-[50px]"
                        >
                            {translations.getWidget}
                        </Link>
                    )}
                </div>

                <div className="mt-5 text-right" style={{ marginTop: '20px' }}>
                    <Link href={`/${lang}`} className="logo-widget inline-block">
                        <Image
                            src="/calculatorscope-logo.svg"
                            alt="Calculator Scope"
                            width={90}
                            height={90}
                            className="object-contain inline-block"
                        />
                    </Link>
                </div>
            </div>

            {showStats && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowStats(false)}>
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                            <h3 className="font-bold text-gray-900">{t.myFlagStats}</h3>
                            <button type="button" onClick={() => setShowStats(false)} aria-label="Close" className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
                        </div>
                        <div className="flex gap-1 px-4 pt-3">
                            {([
                                ['24h', t.last24h],
                                ['7d', t.last7d],
                                ['30d', t.last30d],
                                ['all', t.allTimeLabel],
                            ] as const).map(([key, label]) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setStatsWindow(key)}
                                    className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${statsWindow === key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <div className="px-4 py-3 overflow-y-auto flex-1">
                            {(() => {
                                const windowMs = statsWindow === '24h' ? WINDOW_24H : statsWindow === '7d' ? WINDOW_7D : statsWindow === '30d' ? WINDOW_30D : undefined
                                const totals = getFlagTotals(windowMs)
                                if (totals.length === 0) {
                                    return <div className="text-sm text-gray-500 text-center py-6">{t.noStatsYet}</div>
                                }
                                return (
                                    <ol className="space-y-2">
                                        {totals.map((row, idx) => (
                                            <li key={row.flagSlug} className="flex items-center gap-3">
                                                <span className="w-5 text-xs text-gray-400 font-semibold text-right">{idx + 1}</span>
                                                <img src={`/flags/${row.flagSlug}.svg`} alt={flagDisplayName(row.flagSlug)} className="w-7 h-7 object-contain rounded-sm shrink-0" />
                                                <span className="flex-1 text-sm text-gray-800 truncate">{flagDisplayName(row.flagSlug)}</span>
                                                <span className={`text-sm font-semibold ${row.points < 0 ? 'text-red-600' : 'text-gray-900'}`}>{row.points.toLocaleString()}</span>
                                            </li>
                                        ))}
                                    </ol>
                                )
                            })()}
                        </div>
                        <div className="px-4 py-2.5 border-t border-gray-200 text-xs text-gray-400">{t.myStatsNote}</div>
                    </div>
                </div>
            )}
        </div>
    )
}
